<template>
  <div id="product">
    <section class="bg-cl-primary px20 product-top-section">
      <div class="container">
        <section class="row content_justify m0">
          <div class="col-xs-12 col-md-12 p0">
            <breadcrumbs
              class="pt20 cl-silver"
            />
          </div>
          <div class="col-xs-12 cl-light-gray col-md-8 center-xs middle-xs pt20 image relative p0">
            <product-gallery
              :stylist-notes="getCurrentProduct.stylists_notes"
              :stylist-header="getAllAttributeList('stylists_notes')"
              :offline="getOfflineImage"
              :gallery="getProductGallery"
              :large="getProductLargeGallery"
              :vimeo-id="getCurrentProduct.vimeo_id"
              :configuration="getCurrentProductConfiguration"
              :product="getCurrentProduct"
            />
          </div>
          <div class="col-xs-12 col-md-4 pl30 data">
            <configurable-product v-if="getCurrentProduct.type_id === 'configurable' || getCurrentProduct.type_id === 'sirent'" />
            <simple-product v-else-if="getCurrentProduct.type_id === 'simple'" />
            <shipping-payment-section :product-type="getCurrentProduct.type_id" />
            <product-tabs-desktop-info :title="getAllAttributeList('stylists_notes')" :description="getCurrentProduct.stylists_notes" />
            <product-tabs-desktop-info :title="getAllAttributeList('editors_notes')" :description="getCurrentProduct.editors_notes" />
            <product-tabs-desktop-info :title="getAllAttributeList('fabric')" :description="getCurrentProduct.fabric" />
            <product-tabs-desktop-info :title="isSimple ? $t('Buy risk-free') : $t('RENT NO RISK')" :description="isSimple ? $t('All products available on the E-Garderobe platform undergo a two-step verification for quality and authenticity. This way you can be sure that the product is original and you dont have to worry about defects or damage.') : $t('All the garments at E-Garderobe are without any damages or stains. We always double check quality of our garments before shipping the package. E-Garderobe team makes sure that all rental garments are in perfect condition to meet with customer expectation. All the garments are insured in case of accidental damage or stain.')" />
            <product-tabs-desktop-info :title="$t('Returns and exchanges')" :description="isSimple ? $t('You can return any item purchased from E-Garderobe within 14 days if you have a thought and receive a refund. You can also seamlessly exchange the purchased product for another one from all available on our platform.') : $t('If the rented garment does not fit you right, E-Garderobe offers you an easy exchange for a different size or style within 24 hours. We are here to help you to pick the right size and fit. If you need help, please don’t hesitate to contact our stylist. Our customer support can change the rental date for you. If you want to return your order, we offer you a full refund on E-Garderobe gift card. Please return all unwanted garments within 24 hours with all the security tags attached to the nearest DHL drop off point.')" />
            <product-tabs-desktop-info :title="$t('Shipment')" :description="isSimple ? $t('We guarantee safe and fast shipping throughout Europe. Your order in Poland will be delivered by DHL courier company straight to your home within 3 working days after purchase.') : $t('E-Garderobe offers an express shipping in Poland, Germany and Sweden. If you place your order until 3p.m we can deliver it to you next business day. We use DHL shipping services.')" />
            <product-tabs-desktop-info :title="$t('Covid Information')" :description="$t('All the garments at E-Garderobe are dry-cleaned and ready-to-wear. Our eco-friendly dry-cleaners are using Green Earth technology to clean all the rental garments. E-Garderobe dry-cleaning technology is not harmful to environment and customer’s health.')" />
          </div>
          <div class="col-xs-12 data-mobile visible-xs">
            <div
              class="cl-primary variants visible-xs"
              v-if="getCurrentProduct.type_id =='configurable'"
            >
              <p class="error" v-if="getCurrentProduct.errors && Object.keys(getCurrentProduct.errors).length > 0">
                {{ getCurrentProduct.errors | formatProductMessages }}
              </p>
              <!-- <related-small type="related" :current-product="getCurrentProduct.image" class="" v-if="getCurrentProduct.product_links" /> -->
            </div>
          </div>
        </section>
        <modal-size :modal-size="true" :name="'modal-size'">
          <div slot="header capitalize">
            {{ $t('size guide') }}
          </div>
          <div slot="content" class="table-box">
            <p v-html="sizeGuideTable" />
          </div>
        </modal-size>
      </div>
    </section>
    <div
      v-observe-visibility="{
        callback: visibilityChanged,
        once: true
      }"
    >
      <related-products v-if="seen" type="related" class="pt50 mobile-padding" :heading="$t('Put on with')" :category-id="getFirstClothesChild" />
      <div class="py10" id="7c47852c-7389-4494-ac11-dee2660aea37" />
      <script defer type="application/javascript" :src="`https://trustmate.io/api/widget/7c47852c-7389-4494-ac11-dee2660aea37/script?product=${getCurrentProduct.parentSku}`" />
      <related-products v-if="seen" type="crosssell" class="pt50" :heading="$t('Similar products')" :category-id="getFirstClothesChild" />
    </div>
    <div class="pb40" />
    <product-meta :product="getCurrentProduct" :aggregate-rating="reviewStar" :review-count="reviewCount" :review-arr="reviewArr" :gallery="getUniqProductGallery" />
    <calendar-modal />
    <h1 v-if="getCurrentProduct.instagram_id" class="instagram-title">
      {{ $t('See on instagram') }}
    </h1>
    <div class="pb30" v-if="getCurrentProduct.instagram_id">
      <script src="https://apps.elfsight.com/p/platform.js" type="application/javascript" defer />
      <div :class="'elfsight-app-' + getCurrentProduct.instagram_id + ' container instagram-width'" />
    </div>
    <subscription-modal />
  </div>
</template>

<script>
import config from 'config'
import { vueVimeoPlayer } from 'vue-vimeo-player'
import RelatedProducts from 'theme/components/core/blocks/Product/Related.vue'
import Breadcrumbs from 'theme/components/core/Breadcrumbs.vue'
import ProductGallery from 'theme/components/core/ProductGallery'
import focusClean from 'theme/components/theme/directives/focusClean'
import { mapGetters } from 'vuex'
import { getAvailableFiltersByProduct, getSelectedFiltersByProduct } from '@vue-storefront/core/modules/catalog/helpers/filters'
import { isOptionAvailableAsync } from '@vue-storefront/core/modules/catalog/helpers/index'
import { localizedRoute, currentStoreView } from '@vue-storefront/core/lib/multistore'
import { htmlDecode } from '@vue-storefront/core/filters'
import { catalogHooksExecutors } from '@vue-storefront/core/modules/catalog-next/hooks'
import ProductTabsDesktopInfo from 'theme/components/core/blocks/Product-v2/ProductTabsDesktopInfo.vue'
import ProductMeta from 'theme/components/core/blocks/Product/ProductMeta.vue'
import ModalSize from '../components/core/Modal-size.vue'
import { getThumbnailPath as _thumbnailHelper, onlineHelper, isServer } from '@vue-storefront/core/helpers'
import { categoryResolver } from 'theme/helpers/categoryResolverFunction'
import NoSSR from 'vue-no-ssr'
import ConfigurableProduct from 'theme/components/core/blocks/Product-v2/ConfigurableProduct.vue'
import SimpleProduct from 'theme/components/core/blocks/Product-v2/SimpleProduct.vue'
import CalendarModal from 'theme/components/core/blocks/Product-v2/CalendarModal.vue'
import { registerModule } from '@vue-storefront/core/lib/modules'
import { RecentlyViewedModule } from '@vue-storefront/core/modules/recently-viewed'
import { InstantCheckoutModule } from 'src/modules/vsf-instant-checkout'
import SubscriptionModal from 'theme/components/core/blocks/Product-v2/Rent/components/SubscriptionModal.vue'
import ShippingPaymentSection from 'theme/components/core/blocks/Product-v2/ShippingPaymentSection.vue'

export default {
  components: {
    ModalSize,
    Breadcrumbs,
    ProductGallery,
    ProductTabsDesktopInfo,
    RelatedProducts,
    ProductMeta,
    vueVimeoPlayer,
    'no-ssr': NoSSR,
    ConfigurableProduct,
    SimpleProduct,
    CalendarModal,
    SubscriptionModal,
    ShippingPaymentSection
  },
  directives: { focusClean },
  data () {
    return {
      seen: false,
      reviewStar: 0,
      reviewCount: 0,
      reviewArr: [],
      isStockInfoLoading: false,
      maxQuantity: 0
    }
  },
  async asyncData ({ store, route }) {
    const product = await store.dispatch('product/loadProduct', { parentSku: route.params.parentSku, childSku: route && route.params && route.params.childSku ? route.params.childSku : null })
    const loadBreadcrumbsPromise = store.dispatch('product/loadProductBreadcrumbs', { product })
    if (isServer) await loadBreadcrumbsPromise
    catalogHooksExecutors.productPageVisited(product)
  },
  beforeRouteEnter (to, from, next) {
    if (isServer) {
      next()
    } else {
      next((vm) => {
        vm.$store.commit('ui/routeChanged', { to: to, from: from })
        vm.setAvaliableSourceCodes()
        vm.$store.dispatch('stock/getQuantity')
      })
    }
  },
  beforeCreate () {
    registerModule(RecentlyViewedModule)
    registerModule(InstantCheckoutModule)
  },
  beforeMount () {
    this.$bus.$on('product-after-load', this.addRecentlyViewed)
  },
  created () {
    if (this.getCurrentProduct.product_promo_type) {
      window.setInterval(this.autoIncrementTimer, 1000)
    }
  },
  async mounted () {
    await this.$store.dispatch('recently-viewed/addItem', this.getCurrentProduct)
  },
  beforeDestroy () {
    this.$bus.$off('product-after-load', this.addRecentlyViewed)
  },
  computed: {
    ...mapGetters({
      getCurrentProduct: 'product/getCurrentProduct',
      getProductGallery: 'product/getProductGallery',
      getProductLargeGallery: 'product/getProductLargeGallery',
      getCurrentProductConfiguration: 'product/getCurrentProductConfiguration',
      attributesByCode: 'attribute/attributeListByCode',
      isVirtualInCart: 'cart/isVirtualInCart'
    }),
    getFirstClothesChild () {
      let categoryResolverResult = categoryResolver(this.getCurrentProduct.category, this.$store, config.server.categoryIdsMap.rootCategoryId)
      let firstClothesChild = categoryResolverResult.categoriesIdsFromResolver.length && categoryResolverResult.categoriesIdsFromResolver[0]
      firstClothesChild = firstClothesChild && firstClothesChild.split('/')
      return firstClothesChild && firstClothesChild.length ? Number(firstClothesChild[firstClothesChild.length - 1]) : config.server.categoryIdsMap.rootCategoryId
    },
    designerCheck () {
      return this.getLabelFor('designer') ? this.getLabelFor('designer') : ''
    },
    getMainCategory () {
      return this.getMenuCategories.find(cat => cat.id === config.server.categoryIdsMap.mainCategoryId)
    },
    attrs () {
      return [
        {
          highlight: {
            start: { color: 'blue', fillMode: 'solid' },
            base: { color: 'blue', fillMode: 'light' },
            end: { color: 'blue', fillMode: 'solid' }
          },
          dates: { start: this.getFirstAvaliableDate, span: 1 }
        }
      ]
    },
    sizeGuideTable () {
      return this.validBaners['size_guide'] && this.getCurrentProduct.size_guide
        ? this.validBaners['size_guide'].content
        : ''
    },
    validBaners () {
      return this.$store.state.cmsBlock.baners
    },
    storeView () {
      return currentStoreView()
    },
    isSimple () {
      return this.getCurrentProduct.type_id === 'simple'
    },
    getUniqProductGallery () {
      return this.getProductGallery.reduce((unique, item) => unique.find(elem => elem.src === item.src)
        ? unique
        : [...unique, item], [])
    },
    hostName () {
      return config.server.hostName
    },
    getOfflineImage () {
      return {
        src: this.getThumbnail(this.getCurrentProduct.image, config.products.thumbnails.width, config.products.thumbnails.height),
        error: this.getThumbnail(this.getCurrentProduct.image, config.products.thumbnails.width, config.products.thumbnails.height),
        loading: this.getThumbnail(this.getCurrentProduct.image, config.products.thumbnails.width, config.products.thumbnails.height)
      }
    },
    getAvailableFilters () {
      return getAvailableFiltersByProduct(this.getCurrentProduct)
    },
    listByCode () {
      return this.$store.state.attribute.list_by_code
    }
  },
  methods: {
    // need refactor - duplicate method
    // async getQuantity () {
    //   if (this.isStockInfoLoading) return // stock info is already loading
    //   this.isStockInfoLoading = true
    //   try {
    //     const res = await this.$store.dispatch('stock/check', {
    //       product: this.getCurrentProduct,
    //       qty: this.getCurrentProduct.qty
    //     })
    //     this.maxQuantity = res.qty
    //   } finally {
    //     this.isStockInfoLoading = false
    //   }
    // },
    // need refactor - duplicate method
    setAvaliableSourceCodes () {
      if (this.getCurrentProduct.type_id !== 'configurable' && this.getCurrentProduct.type_id !== 'sirent') return
      if (this.getCurrentProduct.stock && this.getCurrentProduct.stock.source_codes && this.getCurrentProduct.stock.source_codes.length) {
        this.$store.commit('calendar/setAvailableDeliveries', this.getCurrentProduct.stock.source_codes)
      } else {
        this.$store.commit('calendar/setAvailableDeliveries', [])
      }
      this.$store.dispatch('calendar/loadCalendarData', { sku: this.getCurrentProduct.sku })
    },
    async addRecentlyViewed () {
      this.$store.dispatch('recently-viewed/addItem', this.getCurrentProduct)
    },
    getLocaleByStoreId (storeId) {
      for (let storeCode of config.storeViews.mapStoreUrlsFor) {
        if (!config.storeViews[storeCode]) continue
        if (config.storeViews[storeCode].storeId === Number(storeId)) {
          return [
            true,
            config.storeViews[storeCode].i18n.defaultLocale,
            config.storeViews[storeCode].url
          ]
        }
      }
      return [false, null, null]
    },
    getThumbnailPath (url, width, height) {
      return _thumbnailHelper(url, width, height, 'product')
    },
    visibilityChanged (isVisible) {
      if (isVisible && !this.seen) {
        this.seen = true
      }
    },
    getLabelFor (type) {
      if (this.listByCode && this.listByCode[type]) {
        let items = this.listByCode[type].options.filter((option) => { return option.value === String(this.getCurrentProduct[type]) })
        if (items && items.length) {
          return items[0].label
        } else {
          return false
        }
      }
    },
    getAllAttributeList (value) {
      return this.attributesByCode[value] ? this.attributesByCode[value].frontend_label : 'Product info Tab'
    },
    onReady () {
      this.playerReady = true
    },
    isOptionAvailable (option) { // check if the option is available
      const currentConfig = Object.assign({}, this.getCurrentProductConfiguration)
      currentConfig[option.type] = option
      return isOptionAvailableAsync(this.$store, { product: this.getCurrentProduct, configuration: currentConfig })
    }
  },
  metaInfo () {
    let link = []
    let alternates = {}
    for (let storecode of Object.keys(this.getCurrentProduct.store_view_urls)) {
      const storeId = storecode && storecode.split('_')[1]
      if (!storeId) continue
      let [ok, locale, url] = this.getLocaleByStoreId(storeId)
      if (!ok || locale === config.storeViews[this.$store.state.storeView.storeCode].i18n.defaultLocale) continue
      let hreflang = locale === 'en-US' ? 'x-default' : locale
      alternates[hreflang] = {
        rel: 'alternate',
        hreflang: hreflang,
        href: config.server.hostName + url + '/' + this.getCurrentProduct.store_view_urls[`store_${storeId}`]
      }
    }
    link = Object.values(alternates)
    if (Object.keys(this.$route.params).length > 1) {
      link.push({
        rel: 'canonical',
        href: config.server.hostName + this.$route.path
      })
    }
    return {
      htmlAttrs: { lang: config.storeViews[this.$store.state.storeView.storeCode].i18n.defaultLocale },
      link,
      title: htmlDecode(this.getCurrentProduct.meta_title || this.getCurrentProduct.name),
      meta: [
        {
          vmid: 'description',
          name: 'description',
          content: this.getCurrentProduct.meta_description ? htmlDecode(this.getCurrentProduct.meta_description) : htmlDecode(this.getCurrentProduct.description).replace(/<[^>]+>/g, ' ').replace(/\s{2,10}/g, ' ')
        },
        {
          property: 'og:type',
          content: 'product'
        },
        {
          vmid: 'og:title',
          property: 'og:title',
          content: this.getCurrentProduct.meta_title ? htmlDecode(this.getCurrentProduct.meta_title) : htmlDecode(this.getCurrentProduct.name)
        },
        {
          vmid: 'og:locale',
          property: 'og:locale',
          content: this.$store.state.storeView.storeCode ? config.storeViews[this.$store.state.storeView.storeCode].i18n.defaultLocale : ''
        },
        {
          vmid: 'og:description',
          property: 'og:description',
          content: this.getCurrentProduct.meta_description ? htmlDecode(this.getCurrentProduct.meta_description) : htmlDecode(this.getCurrentProduct.description).replace(/<[^>]+>/g, ' ').replace(/\s{2,10}/g, ' ')
        },
        {
          vmid: this.getCurrentProduct.url_path ? 'og:url' : '',
          property: this.getCurrentProduct.url_path ? 'og:url' : '',
          content: this.getCurrentProduct.url_path ? htmlDecode(config.server.hostName + '/' + this.getCurrentProduct.url_path) : ''
        },
        {
          vmid: this.getCurrentProduct.image ? 'og:image' : '',
          property: this.getCurrentProduct.image ? 'og:image' : '',
          content: this.getCurrentProduct.image ? this.getThumbnailPath(this.getCurrentProduct.image, 1200, 1200) : ''
        },
        {
          property: 'og:image:width',
          content: '1200'
        },
        {
          property: 'og:image:height',
          content: '1200'
        },
        {
          property: this.getLabelFor('designer') ? 'og:brand' : '',
          content: this.getLabelFor('designer') ? this.getLabelFor('designer') : ''
        }
      ]
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~theme/css/variables/colors';
@import '~theme/css/helpers/functions/color';
$color-primary: color(primary);
$color-tertiary: color(tertiary);
$color-secondary: color(secondary);
$color-white: color(white);
$bg-secondary: color(secondary, $colors-background);

.add-to-cart-mobile {
  padding: 10px 40px;
}
.product {
  &__add-to-compare {
    display: none;
    @media (min-width: 767px) {
      display: block;
    }
  }
}
.error {
  color: red;
  font-weight: bold;
  // padding-bottom: 15px;
}
.instagram-title {
  margin-top: 25px;
  font-size: 40px;
  font-weight: 500;
  text-align: center;
  @media (max-width: 767px) {
    font-size: 26px;
  }
}
.mobile-video-padding {
  padding-bottom: 150%;
}
.data {
  position: -webkit-sticky;
  position: sticky;
  background-color: white;
  top: 120px;
  padding-top: 20px;
  align-self: flex-start;
  @media (max-width: 767px) {
    order: 1;
    padding-left: 25px;
    padding-right: 24px;
  }
}
.vimeo-video {
  /deep/ iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }
}
.data-mobile {
  ::-webkit-scrollbar {
    width: 0px !important;
    height: 0px !important;
  }
  .scroll-attributes::-webkit-scrollbar { width: 0 !important }
  .scroll-attributes
  {
    overflow-x: scroll;
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch;
    -ms-overflow-style: none;
    overflow: -moz-scrollbars-none;
    display: flex;
    overflow-x: scroll;
  }
  @media (max-width: 767px) {
    order: 3;
  }
}
.breadcrumbs {
  @media (max-width: 767px) {
    margin: 15px 0;
    padding: 15px 0 0 15px;
    order:0;
    font-size: 12px;
    padding-left: 8px;
    margin: 0px;
    white-space: nowrap;
    overflow: hidden;
    -o-text-overflow: ellipsis;
    text-overflow: ellipsis;
    flex-wrap: nowrap !important;
  }
}
.author {
  font: normal normal normal 14px/28px Poppins;
}
.image {
  @media (max-width: 1023px) {
    // margin-bottom: 20px;
  }
}
.instagram-width {
  width: 75% !important;
}
.modal-size-button {
  text-decoration: underline;
  display: flex;
  align-items: flex-end;
  justify-content: start;
  padding-bottom: 7px;
  color: #000000;
  font-size: 12px;
}

.price {
  @media (max-width: 767px) {
    // color: $color-primary;
    margin-top: 10px;
    margin-bottom: 0;
  }
  .special-price {
    @media (max-width: 767px) {
      margin: 0;
      span {
        padding-left: 0;
      }
    }
  }
}
.variants-label {
  @media (max-width: 767px) {
    font-size: 14px;
  }
}
.container {
  @media (max-width: 767px) {
    padding-left: 0;
    padding-right: 0;
  }
}
.imformation-covid {
  margin-top: 20px;
  border-top: 1px solid black;
}
.product-top-section {
  @media (max-width: 767px) {
    padding: 0;
    background-color: $color-white;
  }
}

.price-original {
  text-decoration: line-through;
}

.web-share {
  float: right;
}
.content_justify {
  justify-content: center;
}
.apple-pay-butt {
  padding: 0;
  display: flex;
  height: 45px;
  align-items: center;
  justify-content: center;
  border-radius: 30px;
  background-color: white;
  color: #000000;
  border: 1px solid #000000;
}
@media (min-width: 1024px) and (max-width: 1420px) {
  .image {
    flex-basis: 58.33333333%;
    max-width: 58.33333333%;
    .size-box {
      flex-basis: 19%;
      max-width: 19%;
    }
  }
}
.word-nowrap {
  white-space: nowrap;
}
.promoted-badge {
  margin-bottom: 10px;
  margin-left: 0;
  margin-right: 0;
  justify-content: space-between;
  .counter {
    color: white;
    background-color: #000;
    border-radius: 30px 0 0 30px;
    display: flex;
    justify-content: center;
    padding-left: 30px;
    flex-direction: column;
  }
  .promoted-countdown {
    margin: 0;
  }
  .promoted-title {
    font-size: 18px;
    text-transform: uppercase;
    color: white;
    padding-left: 20px;
    font-weight: 400;
    letter-spacing: 1px;
  }
}

.twisto-baner {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: space-between;
  img {
    width: 100%;
    height: auto;
    object-fit: contain;
  }
}
/deep/ .spinner {
  width: 20px !important;
  height: 20px !important;
  display: flex;
  align-items: center;
}
</style>
